import React, { useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../../../contexts/authContext";
import { Box, Button, TextField, Typography } from "@mui/material";
import { Modal, ModalClose, Sheet } from "@mui/joy";
import DoneIcon from "@mui/icons-material/Done";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import NearMeIcon from "@mui/icons-material/NearMe";
import toast from "react-hot-toast";
import axiosInstance from "../../../api/axios";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Webcam from "react-webcam";
import AddIcon from "@mui/icons-material/Add";

const MobViewAssets = () => {
  const { userData, setRefresh } = useContext(AuthContext);
  const [designId, setDesignId] = useState("");
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [openConfirmLocation, setOpenConfirmLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [openCamera, setOpenCamera] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [openViewDesign, setOpenViewDesign] = useState(false);
  const [designsData, setDesignsData] = useState([]);
  const webcamRef = useRef(null);
  const [activeTab, setActiveTab] = useState("schemes");
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [updateText, setUpdateText] = useState("");
  let storedUserId = localStorage.getItem("userId");
  const handleAddUpdate = (id) => {
    setDesignId(id);
    setUpdateText("");
    setOpenUpdateModal(true);
  };
  useEffect(() => {
    if (!userData || !userData.assets || !Array.isArray(userData.assets)) {
      setRefresh(true); // Trigger data fetch if userData is incomplete
      return;
    }

    const allDesigns = [];

    userData.assets.forEach((asset) => {
      if (Array.isArray(asset.designs)) {
        const activeDesigns = asset.designs.filter(
          (design) =>
            design.status === "active" && design.agent?._id === storedUserId
        );
        allDesigns.push(...activeDesigns);
      }
    });

    let filtered = allDesigns;

    if (search.trim() !== "") {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.designName?.toLowerCase().includes(lowerSearch) ||
          item.assetType?.toLowerCase().includes(lowerSearch) ||
          item.location?.toLowerCase().includes(lowerSearch)
      );
    }

    setFilteredData(filtered);
  }, [search, userData]);

  // Opens the payment modal for a customer
  const handlePhotoUpdate = (id) => {
    setDesignId(id);
    setOpenCamera(true); // open the camera modal
  };

  // When clicking the location icon, update the current location and open the confirm modal.
  const handlenewLocation = (id) => {
    setDesignId(id);
    updateUserLocation();
    setOpenConfirmLocation(true);
  };

  const handleCloseConfirmModal = () => {
    setOpenConfirmLocation(false);
  };
  const uploadCapturedPhoto = async () => {
    setUploading(true);
    try {
      const blob = await (await fetch(imageSrc)).blob();
      const file = new File([blob], "captured.jpg", { type: blob.type });

      const formData = new FormData();
      formData.append("image", file); // must match Multer field name

      const res = await axiosInstance.put(
        `/designs/photo/${designId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.status === 200) {
        toast.success("Photo updated");
        setRefresh(true);
        setOpenCamera(false);
        setImageSrc(null);
      }
    } catch (err) {
      toast.error("Error uploading photo");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  // Fetches the current geographic location of the user using the browser’s geolocation API
  const updateUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error getting user location:", error);
          toast.error("Unable to retrieve your location.");
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
      toast.error("Geolocation is not supported by this browser.");
    }
  };

  // Confirm button handler: Sends a PUT request to update the customer location with proper error handling.
  const handleConfirmAssign = async () => {
    if (!userLocation) {
      toast.error("Kindly Enable Location");
      return;
    }
    try {
      const { lat, lng } = userLocation;
      const res = await axiosInstance.put(`/designs/location/${designId}`, {
        lat,
        long: lng,
      });
      if (res.status === 200) {
        toast.success("Design Location Updated");
        setOpenConfirmLocation(false);
        setDesignId("");
        setRefresh(true);
      }
    } catch (error) {
      console.error("Error updating location:", error);
      toast.error("Error updating location");
    }
  };

  const handleViewDesignUpdates = (id) => () => {
    setDesignId(id);
    let newfilters = filteredData.filter((item) => item._id === id);
    setDesignsData(newfilters[0]);
    setOpenViewDesign(true);
  };
  const handleCloseViewDesign = () => {
    setOpenViewDesign(false);
    setDesignId("");
    setDesignsData([]);
  };

  // Navigates using Google Maps if a customer already has a saved location.
  const handleNavigation = (lat, long) => {
    if (userLocation) {
      const { lat: userLat, lng: userLng } = userLocation;
      const url = `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${lat},${long}&travelmode=driving`;
      window.open(url, "_blank");
    } else {
      toast.error("User location not available");
    }
  };
  useEffect(() => {
    if (!userLocation) {
      updateUserLocation();
    }
  }, [userLocation]);
  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <Typography
          sx={{
            color: "#800001",
            textAlign: "center",
            fontWeight: "bolder",
            fontSize: "20px",
          }}
        >
          Assets
        </Typography>

        <Box>
          <TextField
            placeholder="Search"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: { xs: "100%", sm: "300px" },
              "& .MuiOutlinedInput-input": {
                padding: "10px",
                color: "#800001",
              },
              "& .MuiInputLabel-root": {
                color: "#800001",
              },
              "& .MuiInputLabel-shrink": {
                color: "#800001",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#800001", // Default border
                },
                "&:hover fieldset": {
                  borderColor: "#cc0000", // On hover
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#800001", // On focus
                },
              },
            }}
          />
        </Box>
        {filteredData?.map((item) => (
          <Box
            sx={{
              display: "flex",
              border: "2px solid #800001",
              flexDirection: "column",
              p: 1,
              borderRadius: "10px",
              gap: "2px",
            }}
            key={item._id}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: "bolder", fontSize: "18px" }}>
                  {item.designName}
                </Typography>
                <Typography>{item.location}</Typography>
                <Typography>
                  Due -{" "}
                  {item.nextReminderDate
                    ?.split("T")[0]
                    .split("-")
                    .reverse()
                    .join("-")}
                </Typography>
                <Box sx={{ display: "flex", gap: "2px", alignItems: "center" }}>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      color: "Green",
                      textTransform: "capitalize",
                    }}
                  >
                    {item.designId || "NA"} - {item.status}
                  </Typography>
                  <Button onClick={() => handleAddUpdate(item._id)}>
                    <AddIcon sx={{ color: "#800001" }} />
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Button>
                  <DoneIcon sx={{ color: "green" }} />
                </Button>
                <Button onClick={handleViewDesignUpdates(item._id)}>
                  <VisibilityIcon sx={{ color: "#800001" }} />
                </Button>
                {item.lat && item.long ? (
                  <Button
                    component="a"
                    target="_blank"
                    onClick={() => handleNavigation(item.lat, item.long)}
                  >
                    <NearMeIcon sx={{ color: "#EEC056" }} />
                  </Button>
                ) : (
                  <Button onClick={() => handlenewLocation(item._id)}>
                    <MyLocationIcon sx={{ color: "#EEC056" }} />
                  </Button>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                borderTop: "1px solid #800001",
                justifyContent: "space-between",
                alignItems: "center",
                pt: 1,
              }}
            >
              <Typography sx={{ fontWeight: "bold", color: "#800001" }}>
                {item.assetType}
              </Typography>
              <Button
                onClick={() => handlePhotoUpdate(item._id)}
                sx={{
                  backgroundColor: "#800001",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "12px",
                  "&:hover": {
                    backgroundColor: "#fbbd2c",
                    color: "#000",
                  },
                }}
              >
                Update Photo
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
      <>
        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
          open={openConfirmLocation}
          onClose={handleCloseConfirmModal}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Sheet
            variant="outlined"
            sx={{
              maxWidth: 200,
              width: "100%",
              bgcolor: "#FEDDDD",
              p: 2,
              borderRadius: "md",
              boxShadow: "lg",
              m: 2,
            }}
          >
            {/* <ModalClose
              sx={{
                m: 1,
                "& svg": { color: "#800001" },
                "&:hover": {
                  backgroundColor: "#800001",
                  "& svg": { color: "#fff" },
                },
              }}
              onClick={handleCloseConfirmModal}
            /> */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography>Confirm Location?</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: "10px", marginTop: 2 }}>
                <Button
                  sx={{
                    backgroundColor: "#800001",
                    color: "#fff",
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": { backgroundColor: "#fbbd2c", color: "#000" },
                  }}
                  onClick={handleCloseConfirmModal}
                >
                  Cancel
                </Button>
                <Button
                  sx={{
                    backgroundColor: "#800001",
                    color: "#fff",
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": { backgroundColor: "#fbbd2c", color: "#000" },
                  }}
                  onClick={handleConfirmAssign}
                >
                  Confirm
                </Button>
              </Box>
            </Box>
          </Sheet>
        </Modal>
      </>
      {openViewDesign && (
        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
          open={openViewDesign}
          onClose={() => setOpenViewDesign(false)}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Sheet
            variant="outlined"
            sx={{
              maxWidth: 300,
              width: "100%",
              bgcolor: "#FEDDDD",
              p: 2,
              borderRadius: "md",
              boxShadow: "lg",
              m: 2,
              height: "80vh",
              overflowY: "auto",
            }}
          >
            <ModalClose
              sx={{
                m: 1,
                "& svg": { color: "#800001" },
                "&:hover": {
                  backgroundColor: "#800001",
                  "& svg": { color: "#fff" },
                },
              }}
              onClick={handleCloseViewDesign}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography>Design Updates</Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  mb: 2,
                  mt: 2,
                  backgroundColor: "#fff",
                  borderRadius: 10,
                  width: "100%",
                }}
              >
                <Button
                  onClick={() => setActiveTab("schemes")}
                  sx={{
                    flex: 1,
                    backgroundColor:
                      activeTab === "schemes" ? "#800001" : "#fff",
                    color: activeTab === "schemes" ? "#fff" : "#800001",
                    borderRadius: 10,
                    "&:hover": {
                      backgroundColor:
                        activeTab === "schemes" ? "#800001" : "#ffe8e8",
                    },
                  }}
                >
                  Designs
                </Button>

                <Button
                  onClick={() => setActiveTab("updates")}
                  sx={{
                    flex: 1,
                    backgroundColor:
                      activeTab === "updates" ? "#800001" : "#fff",
                    color: activeTab === "updates" ? "#fff" : "#800001",
                    borderRadius: 10,
                    "&:hover": {
                      backgroundColor:
                        activeTab === "updates" ? "#800001" : "#ffe8e8",
                    },
                  }}
                >
                  Updates
                </Button>
              </Box>
              {activeTab === "schemes" && (
                <>
                  {designsData?.images?.map((image, index) => (
                    <Box key={index} sx={{ margin: "10px 0" }}>
                      <Typography sx={{ marginTop: "5px" }}>
                        {image.createdAt
                          ?.split("T")[0]
                          .split("-")
                          .reverse()
                          .join("-") || "No update provided"}
                      </Typography>
                      <img
                        // src={`${process.env.REACT_APP_API_URL}/uploads/${image.image}`}
                        src={`https://sakthiapi.mapsapp.in/uploads/${image.image}`}
                        // src={`http://localhost:4002/uploads/${image.image}`}

                        alt={`Design ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "auto",
                          borderRadius: "8px",
                        }}
                      />
                      <Typography
                        sx={{ textAlign: "center", marginTop: "5px" }}
                      >
                        {image.update || "No update provided"}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
              {activeTab === "updates" && (
                <Box
                  sx={{
                    backgroundColor: "#fff3f3",
                    p: 2,
                    borderRadius: 2,
                    mt: 1,
                    width: "90%",
                  }}
                >
                  <Typography sx={{ color: "#800001", fontWeight: "bold" }}>
                    Updates
                  </Typography>

                  {designsData?.updates?.length > 0 ? (
                    designsData.updates
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((u, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            borderBottom: "1px solid #ddd",
                            py: 1,
                            mt: 1,
                          }}
                        >
                          <Typography sx={{ fontSize: "14px" }}>
                            {u.text}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "12px", color: "gray" }}
                            >
                              {userData?.users?.find(
                                (usr) => usr._id === u.added_by
                              )?.name || "Unknown"}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "12px", color: "gray" }}
                            >
                              {new Date(u.date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                      ))
                  ) : (
                    <Typography sx={{ fontSize: "14px", mt: 1, color: "gray" }}>
                      No updates added.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Sheet>
        </Modal>
      )}
      <Modal open={openUpdateModal} onClose={() => setOpenUpdateModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: 300, sm: 400 },
            bgcolor: "#FEDDDD",
            p: 3,
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Typography sx={{ mb: 1, fontWeight: 600 }}>Add Update</Typography>

          <TextField
            multiline
            rows={4}
            fullWidth
            value={updateText}
            sx={{ bgcolor: "#fff" }}
            onChange={(e) => setUpdateText(e.target.value)}
            placeholder="Enter update notes..."
          />

          <Button
            sx={{
              mt: 2,
              backgroundColor: "#800001",
              color: "white",
              width: "100%",
              "&:hover": { backgroundColor: "#a00001" },
            }}
            onClick={async () => {
              await axiosInstance.post(`/designs/addupdates/${designId}`, {
                updates: updateText,
                added_by: localStorage.getItem("userId"),
              });
              toast.success("Updates added");
              setRefresh(true);
              setOpenUpdateModal(false);
            }}
          >
            Save Updates
          </Button>
        </Box>
      </Modal>

      {openCamera && (
        <Modal
          open={openCamera}
          onClose={() => {
            setOpenCamera(false);
            setImageSrc(null);
            setUploading(false);
          }}
        >
          <Sheet
            variant="outlined"
            sx={{
              width: 300,
              bgcolor: "#fff",
              p: 2,
              m: "auto",
              mt: 5,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            {!imageSrc ? (
              <>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  width="100%"
                  height="100%"
                  videoConstraints={{
                    facingMode: "environment",
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Button
                    sx={{
                      backgroundColor: "#800001",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#fbbd2c", color: "#000" },
                    }}
                    onClick={() => {
                      setOpenCamera(false);
                      setImageSrc(null);
                      setUploading(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={uploading}
                    sx={{
                      backgroundColor: "#800001",
                      color: "#fff",
                      fontWeight: "bold",
                      "&:hover": { backgroundColor: "#fbbd2c", color: "#000" },
                    }}
                    onClick={() => {
                      const image = webcamRef.current.getScreenshot();
                      if (image) setImageSrc(image);
                    }}
                  >
                    Capture Photo
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <img
                  src={imageSrc}
                  alt="Captured"
                  style={{ width: "100%", borderRadius: 8 }}
                />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    disabled={uploading}
                    sx={{
                      backgroundColor: "#800001",
                      color: "#fff",
                      fontWeight: "bold",
                      "&:hover": { backgroundColor: "#fbbd2c", color: "#000" },
                    }}
                    onClick={uploadCapturedPhoto}
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </Button>
                  <Button
                    disabled={uploading}
                    sx={{
                      backgroundColor: "#ccc",
                      color: "#000",
                    }}
                    onClick={() => setImageSrc(null)}
                  >
                    Retake
                  </Button>
                </Box>
              </>
            )}
          </Sheet>
        </Modal>
      )}
    </>
  );
};

export default MobViewAssets;
